/**
 * Tables out of the room, as CSV.
 *
 * Pure shaping here, so what lands in the file is testable without a DOM: a
 * report is a derived view, and the download should be exactly what is on the
 * screen — the same rows, the same lens, the same orientation — rather than a
 * second, subtly different export path that drifts from what you read.
 *
 * There used to be eight shapers below this, one per report, each rebuilding
 * its report's columns by hand — the exact duplication that let a file export
 * the wrong values under the right headings. Every table is column-driven now,
 * so `tableCsv` is the only shaper there is, and it reads what the table read.
 */
import { csvRows, sortRows, type Column, type Sort } from './tableColumns';

/** Cells that spreadsheets execute rather than display.
 *
 *  Every value in these tables came off somebody else's web page, so a cell
 *  beginning =, +, - or @ is a formula the moment the file is opened in Excel
 *  or Sheets. Prefixing with an apostrophe is the standard defusing: the cell
 *  still reads correctly, it just is not run. */
const RISKY = /^[=+\-@\t\r]/;

export function escapeCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  const safe = RISKY.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

/** THE GENERIC SHAPER. A column knows how to read itself, so any table built
 *  from columns exports without a shaper of its own — and cannot disagree with
 *  what is on screen, because it is reading the same `value` the table sorted
 *  by and the picker filtered. */
export function tableCsv<T>(columns: Column<T>[], rows: T[], sort: Sort | null = null): string {
  return toCsv(csvRows(columns, sortRows(rows, columns, sort)));
}

export function toCsv(rows: unknown[][]): string {
  // CRLF: what every spreadsheet expects, and what RFC 4180 says.
  return rows.map(r => r.map(escapeCell).join(',')).join('\r\n');
}

/** A filename a person can find again: what it is, which room, which day. */
export function csvName(room: string, table: string, on = new Date()): string {
  const slug = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'boardroom';
  const day = on.toISOString().slice(0, 10);
  return `${slug(room)}-${slug(table)}-${day}.csv`;
}
